import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentProposalUpgrade,
  AppointmentProposalUpgradeInternal,
} from './appointmentProposalUpgrades.models';
import {
  appointmentProposalUpgradesReducer,
  AppointmentProposalUpgradesState,
  initState,
} from './appointmentProposalUpgrades.reducer';

type CreatePayload = Omit<
  AppointmentProposalUpgradeInternal,
  | 'appointmentId'
  | 'appointmentProposalId'
  | 'appointmentProposalUpgradeId'
  | 'status'
  | 'entityType'
>;

interface AppointmentProposalUpgradesContextProps {
  proposalUpgrades: AppointmentProposalUpgrade[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchAppointmentProposalUpgradesProducts: (
    appointmentId: string,
    appointmentProposalId: string
  ) => Promise<unknown[]>;
  createAppointmentProposalUpgrade: (
    appointmentId: string,
    appointmentProposalId: string,
    create: CreatePayload
  ) => Promise<void>;
  deleteAppointmentProposalUpgrade: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalUpgradeId: string
  ) => Promise<void>;
}

const AppointmentProposalUpgradesContext = createContext<
  AppointmentProposalUpgradesContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentProposalUpgradesState>;
}>;

export const AppointmentProposalUpgradesProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentProposalUpgradesReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const proposalUpgrades = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (u) =>
            u.appointmentProposalId === state.selectedProposalId &&
            u.status !== 'DELETED'
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids, state.selectedProposalId]
  );

  const fetchAppointmentProposalUpgradesProducts: AppointmentProposalUpgradesContextProps['fetchAppointmentProposalUpgradesProducts'] =
    useCallback(
      async (appointmentId: string, appointmentProposalId: string) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return [];
        }
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS',
          payload: { appointmentProposalId },
        });
        try {
          const appointmentProposalUpgrades = await httpClient.get<
            AppointmentProposalUpgrade[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/upgrades/products`
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_SUCCESS',
            payload: { appointmentProposalUpgrades, appointmentProposalId },
          });

          return appointmentProposalUpgrades;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_UPGRADES_PRODUCTS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient, isOnline]
    );

  const createAppointmentProposalUpgrade = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      create: CreatePayload
    ) => {
      dispatch({ type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE' });
      try {
        const appointmentProposalUpgrade =
          await httpClient.post<AppointmentProposalUpgrade>(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/upgrades`,
            {
              ...create,
              status: 'ACTIVE',
              entityType: 'APPOINTMENT_PROPOSAL_UPGRADE',
            }
          );
        dispatch({
          type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS',
          payload: { appointmentProposalUpgrade },
        });
      } catch (error) {
        dispatch({
          type: 'CREATE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient]
  );

  const deleteAppointmentProposalUpgrade = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalUpgradeId: string
    ) => {
      dispatch({
        type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE',
        payload: { appointmentProposalUpgradeId },
      });

      try {
        await httpClient.delete(
          `/appointments/${appointmentId}/proposals/${appointmentProposalId}/upgrades/${appointmentProposalUpgradeId}`
        );
        dispatch({
          type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_SUCCESS',
          payload: { appointmentProposalUpgradeId },
        });
      } catch (error) {
        dispatch({
          type: 'DELETE_APPOINTMENT_PROPOSAL_UPGRADE_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient]
  );

  return (
    <AppointmentProposalUpgradesContext.Provider
      value={{
        proposalUpgrades,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchAppointmentProposalUpgradesProducts,
        createAppointmentProposalUpgrade,
        deleteAppointmentProposalUpgrade,
      }}
    >
      {children}
    </AppointmentProposalUpgradesContext.Provider>
  );
};

export const useAppointmentProposalUpgrades = () => {
  const context = useContext(AppointmentProposalUpgradesContext);
  if (context === undefined) {
    throw new Error(
      'useAppointmentProposalUpgrades must be used within a AppointmentProposalUpgradesProvider'
    );
  }
  return context;
};
